import { useEffect, useRef } from "react";

// Lightweight canvas fireworks overlay. Auto-stops on first scroll and after ~12s.
export default function Fireworks() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let DPR = Math.min(window.devicePixelRatio || 1, 2);
    let W, H;
    const resize = () => {
      W = canvas.width = canvas.clientWidth * DPR;
      H = canvas.height = canvas.clientHeight * DPR;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = [];
    const rockets = [];

    const palettes = [
      ["#FFD37A", "#FFB347", "#FF7A45", "#FFFFFF"],
      ["#E0C9A0", "#F2D7A1", "#FFE7B3", "#FFFFFF"],
      ["#B3D9C7", "#88C2A8", "#5FB191", "#FFFFFF"],
      ["#FFB6C1", "#FF85A1", "#FF5C8A", "#FFFFFF"],
      ["#B89968", "#D4B080", "#F0CC9A", "#FFFFFF"],
    ];

    function spawnRocket() {
      const x = (0.2 + Math.random() * 0.6) * W;
      const targetY = (0.18 + Math.random() * 0.32) * H;
      rockets.push({
        x,
        y: H + 6 * DPR,
        vx: (Math.random() - 0.5) * 0.6 * DPR,
        vy: -(8 + Math.random() * 2) * DPR,
        targetY,
        trail: [],
        palette: palettes[Math.floor(Math.random() * palettes.length)],
      });
    }

    function burst(x, y, palette) {
      const count = 70 + Math.floor(Math.random() * 40);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.2;
        const speed = (1.5 + Math.random() * 3.2) * DPR;
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          decay: 0.012 + Math.random() * 0.012,
          size: (1.4 + Math.random() * 1.6) * DPR,
          color: palette[Math.floor(Math.random() * palette.length)],
        });
      }
    }

    let active = true;
    let lastShot = 0;
    let interval = 700;
    const startTime = performance.now();
    const maxDuration = 12000; // hard stop after 12s

    function tick(t) {
      if (!active) return;

      // soft fade trails for sparkle effect
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.18)";
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";

      // spawn rockets at intervals; intensify briefly then taper
      const age = t - startTime;
      if (age > maxDuration) {
        stop();
        return;
      }
      if (age > 8000) interval = 1200;
      if (t - lastShot > interval) {
        spawnRocket();
        if (Math.random() < 0.5) spawnRocket();
        lastShot = t;
      }

      // rockets
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;
        r.vy += 0.12 * DPR; // gravity slows it
        r.trail.push({ x: r.x, y: r.y });
        if (r.trail.length > 10) r.trail.shift();

        // trail
        for (let j = 0; j < r.trail.length; j++) {
          const p = r.trail[j];
          ctx.beginPath();
          ctx.arc(p.x, p.y, (1 + j / 4) * DPR, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,235,180,${(j / r.trail.length) * 0.6})`;
          ctx.fill();
        }

        if (r.y <= r.targetY || r.vy >= 0) {
          burst(r.x, r.y, r.palette);
          rockets.splice(i, 1);
        }
      }

      // particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.04 * DPR; // gravity
        p.vx *= 0.99;
        p.vy *= 0.99;
        p.life -= p.decay;
        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
        const c = p.color;
        // hex → rgba with life alpha
        const r2 = parseInt(c.slice(1, 3), 16);
        const g2 = parseInt(c.slice(3, 5), 16);
        const b2 = parseInt(c.slice(5, 7), 16);
        ctx.fillStyle = `rgba(${r2},${g2},${b2},${p.life})`;
        ctx.fill();
      }

      ctx.globalCompositeOperation = "source-over";
      rafId = requestAnimationFrame(tick);
    }

    let rafId = requestAnimationFrame(tick);

    function stop() {
      if (!active) return;
      active = false;
      cancelAnimationFrame(rafId);
      canvas.style.transition = "opacity 1s ease";
      canvas.style.opacity = "0";
      setTimeout(() => {
        if (canvas) canvas.style.display = "none";
      }, 1100);
    }

    function onScroll() {
      if (window.scrollY > 40) stop();
    }
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      active = false;
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="fireworks-canvas"
      className="pointer-events-none absolute inset-0 z-[5] h-full w-full"
      aria-hidden="true"
    />
  );
}
